import { useAtom } from '@reatom/npm-react'
import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { useBorderBoxSizeCallback } from '@blade/ui-kit'
import {
  EXPLORE_SCREEN_TABS,
  ExploreTypeMap,
} from '@root/features/explore/model/explore-feed-type'
import { useTranslation } from '@root/shared/localization'
import { UnderlineTabs } from '@root/shared/ui-kit'

import { exploreTabAtom } from '../../atoms/explore-tab-atom'

import { ExploreScreenTabsSelect } from './explore-screen-tabs-select'

import type { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'

const TAB_OVERFLOW_OFFSET = 0

export const ExploreScreenTabs = memo(() => {
  const { t } = useTranslation()
  const [tab, setTab] = useAtom(exploreTabAtom)
  const rootRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLButtonElement>(null)
  const [tabOrder, setTabOrder] = useState(EXPLORE_SCREEN_TABS)
  const [visibleCount, setVisibleCount] = useState(EXPLORE_SCREEN_TABS.length)

  const handleHiddenFeedSelect = useCallback(
    (feed: ExploreFeedType) => {
      setTab(feed)
      setTabOrder((order) => [feed, ...order.filter((f) => f !== feed)])
    },
    [setTab],
  )

  const update = useCallback(() => {
    const rootEl = rootRef.current
    if (!rootEl) {
      return
    }

    const rootRight = rootEl.getBoundingClientRect().right
    const select = selectRef.current
    const rights = [...rootEl.querySelectorAll('button')]
      .filter((el) => el !== select)
      .map((el) => el.getBoundingClientRect().right)

    const overflow = rights.some((r) => r > rootRight + TAB_OVERFLOW_OFFSET)
    const limit =
      overflow && select ? select.getBoundingClientRect().left : rootRight

    setVisibleCount(
      rights.filter((r) => r <= limit + TAB_OVERFLOW_OFFSET).length,
    )
  }, [])

  useLayoutEffect(update, [update, t, tabOrder])
  useBorderBoxSizeCallback(rootRef, update)

  const hiddenFeeds = tabOrder.slice(visibleCount)

  return (
    <RootOverflow ref={rootRef}>
      <Root value={tab} onChange={setTab}>
        {tabOrder.map((feed, index) => {
          const { Icon, label, color } = ExploreTypeMap[feed]

          return (
            <Tab key={feed} value={feed} disabled={index >= visibleCount}>
              <TabLabel>
                <TabIcon $color={color}>
                  <Icon size={16} />
                </TabIcon>
                {t(label)}
              </TabLabel>
            </Tab>
          )
        })}
      </Root>
      {hiddenFeeds.length > 0 && (
        <ExploreScreenTabsSelect
          ref={selectRef}
          hiddenFeeds={hiddenFeeds}
          onSelect={handleHiddenFeedSelect}
        />
      )}
    </RootOverflow>
  )
})

const RootOverflow = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
`

const Root = styled(UnderlineTabs<ExploreFeedType>)`
  --tab-font-size: var(--typography-body-4-medium-font-size);
  --tab-padding-inline: 12px;
  --tab-height: 32px;

  justify-content: start;
  border-bottom: none;
  padding-inline-start: 12px;

  border-bottom: 2px solid var(--appearance-1);

  button {
    margin-bottom: -2px;

    > span {
      margin-top: 3px;
    }
  }
`

const Tab = styled(UnderlineTabs.Tab)`
  white-space: nowrap;

  &[aria-disabled='true'] {
    opacity: 0;
    pointer-events: none;
  }
`

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const TabIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  flex-shrink: 0;

  svg {
    color: ${({ $color }) => $color};
  }
`
